/**
 * RosterExportService - Handles Excel export functionality for Roster
 * 
 * This service manages all export operations for the Roster feature,
 * following the same pattern as the ForecastVsActuals ExportService.
 * 
 * Key responsibilities:
 * - Export roster data to Excel with multiple tabs
 * - Format data for export (members by outcome, outcomes by member)
 * - Handle tag ID to name conversion for export
 * - Generate comprehensive allocation reports
 * - Manage file naming and download
 */

import * as XLSX from 'xlsx';
import { getLocationDisplayName } from '../utils/rosterCalculations';

class RosterExportService {
  constructor() {
    this.exportStats = {
      lastExportTime: null,
      totalExports: 0,
      lastExportSize: 0
    };
  }

  /**
   * Export roster data to Excel with multiple tabs
   */
  exportToExcel(roster, outcomeAllocations, getMemberOutcomeAllocations, outcomes, availableTags, formatMemberFunction) {
    try {
      console.log('📊 RosterExportService: Starting Excel export...');
      console.log('📊 RosterExportService: Data summary:', {
        roster: roster.length,
        outcomeAllocations: outcomeAllocations.length,
        outcomes: outcomes.length,
        availableTags: availableTags.length
      });

      // Tab 1: Members with expanded outcome allocations
      const memberRows = this.generateMemberRows(roster, getMemberOutcomeAllocations, outcomes, availableTags, formatMemberFunction);
      
      // Tab 2: Outcomes with allocated members
      const outcomeRows = this.generateOutcomeRows(outcomeAllocations, roster, availableTags, formatMemberFunction);
      
      // Tab 3: Summary statistics
      const summaryRows = this.generateSummaryRows(roster, outcomeAllocations, availableTags);
      
      // Create workbook with multiple tabs
      const workbook = XLSX.utils.book_new();
      
      // Add Members tab
      const membersWorksheet = XLSX.utils.json_to_sheet(memberRows);
      this.formatWorksheet(membersWorksheet, 'members');
      XLSX.utils.book_append_sheet(workbook, membersWorksheet, 'Members by Outcome');
      
      // Add Outcomes tab
      const outcomesWorksheet = XLSX.utils.json_to_sheet(outcomeRows);
      this.formatWorksheet(outcomesWorksheet, 'outcomes');
      XLSX.utils.book_append_sheet(workbook, outcomesWorksheet, 'Outcomes by Member');
      
      // Add Summary tab
      const summaryWorksheet = XLSX.utils.json_to_sheet(summaryRows);
      this.formatWorksheet(summaryWorksheet, 'summary');
      XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Summary Statistics');
      
      // Generate filename with current date
      const currentDate = new Date().toISOString().split('T')[0];
      const fileName = `Roster_Allocations_${currentDate}.xlsx`;
      
      // Export file
      XLSX.writeFile(workbook, fileName);
      
      // Update stats
      this.exportStats.lastExportTime = new Date();
      this.exportStats.totalExports++;
      this.exportStats.lastExportSize = memberRows.length + outcomeRows.length + summaryRows.length;
      
      console.log('✅ RosterExportService: Excel export completed:', fileName);
      console.log('📊 RosterExportService: Export stats:', {
        memberRows: memberRows.length,
        outcomeRows: outcomeRows.length,
        summaryRows: summaryRows.length,
        totalRows: this.exportStats.lastExportSize
      });
      
      return {
        success: true,
        fileName,
        stats: {
          memberRows: memberRows.length,
          outcomeRows: outcomeRows.length,
          summaryRows: summaryRows.length
        }
      };
      
    } catch (error) {
      console.error('❌ RosterExportService: Error exporting to Excel:', error);
      throw new Error(`Failed to export roster data: ${error.message}`);
    }
  }

  /**
   * Generate member rows for export (Members by Outcome tab)
   */
  generateMemberRows(roster, getMemberOutcomeAllocations, outcomes, availableTags, formatMemberFunction) {
    const memberRows = [];
    
    // Process ALL roster members (not just filtered/displayed ones)
    roster.forEach(member => {
      const memberOutcomes = getMemberOutcomeAllocations(member.id);
      const memberApplications = new Set();
      const memberTags = new Set();
      
      // Collect applications and tags
      Object.values(memberOutcomes).forEach(outcome => {
        if (outcome.applications) {
          outcome.applications.forEach(app => memberApplications.add(app));
        }
      });
      
      // Convert tag IDs to tag names for export
      this.convertMemberTagsForExport(member.tags, availableTags, memberTags);
      
      // Helper function to get project code for outcome
      const getProjectCodeForOutcome = (outcomeName) => {
        const outcome = outcomes.find(o => o.name === outcomeName);
        return outcome?.project_code || '';
      };
      
      // If member has outcome allocations, create rows for each outcome
      if (Object.keys(memberOutcomes).length > 0) {
        Object.values(memberOutcomes).forEach(outcome => {
          const hasStaffedData = outcome.staffed.some(val => val > 0);
          if (hasStaffedData) {
            const projectCode = getProjectCodeForOutcome(outcome.outcome_name);
            const outcomeWithProject = projectCode ? 
              `${outcome.outcome_name} (${projectCode})` : 
              outcome.outcome_name;
            
            memberRows.push({
              'Member Name': member.name,
              'Company': member.company,
              'Location': getLocationDisplayName(member.location),
              'Function': formatMemberFunction(member),
              'Role': member.role || '',
              'Focus': member.focus || '',
              'Department': member.department || '',
              'Applications': Array.from(memberApplications).join(', '),
              'Tags': Array.from(memberTags).join(', '),
              'Outcome Name': outcomeWithProject,
              'Project Code': projectCode,
              'Outcome Applications': outcome.applications ? outcome.applications.join(', ') : '',
              // Convert percentages to decimals (75% -> 0.75)
              'Jan': outcome.staffed[0] > 0 ? outcome.staffed[0] / 100 : 0,
              'Feb': outcome.staffed[1] > 0 ? outcome.staffed[1] / 100 : 0,
              'Mar': outcome.staffed[2] > 0 ? outcome.staffed[2] / 100 : 0,
              'Apr': outcome.staffed[3] > 0 ? outcome.staffed[3] / 100 : 0,
              'May': outcome.staffed[4] > 0 ? outcome.staffed[4] / 100 : 0,
              'Jun': outcome.staffed[5] > 0 ? outcome.staffed[5] / 100 : 0,
              'Jul': outcome.staffed[6] > 0 ? outcome.staffed[6] / 100 : 0,
              'Aug': outcome.staffed[7] > 0 ? outcome.staffed[7] / 100 : 0,
              'Sep': outcome.staffed[8] > 0 ? outcome.staffed[8] / 100 : 0,
              'Oct': outcome.staffed[9] > 0 ? outcome.staffed[9] / 100 : 0,
              'Nov': outcome.staffed[10] > 0 ? outcome.staffed[10] / 100 : 0,
              'Dec': outcome.staffed[11] > 0 ? outcome.staffed[11] / 100 : 0
            });
          }
        });
      } else {
        // Member has no allocations - add them with zero allocations
        const memberTagNames = [];
        this.convertMemberTagsForExport(member.tags, availableTags, new Set(memberTagNames));
        
        memberRows.push({
          'Member Name': member.name,
          'Company': member.company,
          'Location': getLocationDisplayName(member.location),
          'Function': formatMemberFunction(member),
          'Role': member.role || '',
          'Focus': member.focus || '',
          'Department': member.department || '',
          'Applications': '',
          'Tags': Array.from(memberTagNames).join(', '),
          'Outcome Name': 'No Allocation',
          'Project Code': '',
          'Outcome Applications': '',
          'Jan': 0, 'Feb': 0, 'Mar': 0, 'Apr': 0, 'May': 0, 'Jun': 0,
          'Jul': 0, 'Aug': 0, 'Sep': 0, 'Oct': 0, 'Nov': 0, 'Dec': 0
        });
      }
    });
    
    return memberRows;
  }

  /**
   * Generate outcome rows for export (Outcomes by Member tab)
   */
  generateOutcomeRows(outcomeAllocations, roster, availableTags, formatMemberFunction) {
    const outcomeRows = [];
    
    outcomeAllocations.forEach(outcome => {
      if (outcome.staffed_roles && outcome.staffed_roles.length > 0) {
        outcome.staffed_roles.forEach(role => {
          const member = roster.find(m => m.id === role.member_id);
          if (member) {
            // Convert tag IDs to tag names for export
            const memberTagNames = [];
            this.convertMemberTagsForExport(member.tags, availableTags, new Set(memberTagNames));
            const memberTags = Array.from(memberTagNames).join(', ');
            const outcomeApplications = outcome.applications ? outcome.applications.join(', ') : '';
            
            outcomeRows.push({
              'Outcome Name': outcome.outcome_name,
              'Outcome Applications': outcomeApplications,
              'Member Name': member.name,
              'Company': member.company,
              'Location': getLocationDisplayName(member.location),
              'Function': formatMemberFunction(member),
              'Role': member.role || '',
              'Focus': member.focus || '',
              'Department': member.department || '',
              'Tags': memberTags,
              'Role Name': role.role_name || '',
              'Milestone': role.milestone_name || '',
              // Convert percentages to decimals (75% -> 0.75)
              'Jan': role.monthly_allocations && role.monthly_allocations[0] > 0 ? role.monthly_allocations[0] / 100 : 0,
              'Feb': role.monthly_allocations && role.monthly_allocations[1] > 0 ? role.monthly_allocations[1] / 100 : 0,
              'Mar': role.monthly_allocations && role.monthly_allocations[2] > 0 ? role.monthly_allocations[2] / 100 : 0,
              'Apr': role.monthly_allocations && role.monthly_allocations[3] > 0 ? role.monthly_allocations[3] / 100 : 0,
              'May': role.monthly_allocations && role.monthly_allocations[4] > 0 ? role.monthly_allocations[4] / 100 : 0,
              'Jun': role.monthly_allocations && role.monthly_allocations[5] > 0 ? role.monthly_allocations[5] / 100 : 0,
              'Jul': role.monthly_allocations && role.monthly_allocations[6] > 0 ? role.monthly_allocations[6] / 100 : 0,
              'Aug': role.monthly_allocations && role.monthly_allocations[7] > 0 ? role.monthly_allocations[7] / 100 : 0,
              'Sep': role.monthly_allocations && role.monthly_allocations[8] > 0 ? role.monthly_allocations[8] / 100 : 0,
              'Oct': role.monthly_allocations && role.monthly_allocations[9] > 0 ? role.monthly_allocations[9] / 100 : 0,
              'Nov': role.monthly_allocations && role.monthly_allocations[10] > 0 ? role.monthly_allocations[10] / 100 : 0,
              'Dec': role.monthly_allocations && role.monthly_allocations[11] > 0 ? role.monthly_allocations[11] / 100 : 0
            });
          }
        });
      }
    });
    
    return outcomeRows;
  }

  /**
   * Generate summary statistics for export
   */
  generateSummaryRows(roster, outcomeAllocations, availableTags) {
    const summaryRows = [];
    
    // Overall statistics
    const assignedMemberIds = new Set();
    outcomeAllocations.forEach(outcome => {
      if (outcome.staffed_roles) {
        outcome.staffed_roles.forEach(role => {
          if (role.member_id) assignedMemberIds.add(role.member_id);
        });
      }
    });
    
    summaryRows.push({
      'Metric': 'Total Members',
      'Value': roster.length,
      'Percentage': '100%',
      'Description': 'Total number of members in the roster'
    });
    
    summaryRows.push({
      'Metric': 'Assigned Members',
      'Value': assignedMemberIds.size,
      'Percentage': roster.length > 0 ? `${Math.round((assignedMemberIds.size / roster.length) * 100)}%` : '0%',
      'Description': 'Members with at least one outcome allocation'
    });
    
    summaryRows.push({
      'Metric': 'Unassigned Members',
      'Value': roster.length - assignedMemberIds.size,
      'Percentage': roster.length > 0 ? `${Math.round(((roster.length - assignedMemberIds.size) / roster.length) * 100)}%` : '0%',
      'Description': 'Members with no outcome allocations'
    });
    
    summaryRows.push({
      'Metric': 'Total Outcomes',
      'Value': outcomeAllocations.length,
      'Percentage': '',
      'Description': 'Total number of outcomes with staffing data'
    });
    
    // Company breakdown
    const companyBreakdown = {};
    roster.forEach(member => {
      companyBreakdown[member.company] = (companyBreakdown[member.company] || 0) + 1;
    });
    
    Object.entries(companyBreakdown).forEach(([company, count]) => {
      summaryRows.push({
        'Metric': `${company} Company Members`,
        'Value': count,
        'Percentage': roster.length > 0 ? `${Math.round((count / roster.length) * 100)}%` : '0%',
        'Description': `Members in ${company} company`
      });
    });
    
    // Function breakdown
    const functionBreakdown = {};
    roster.forEach(member => {
      const func = member.function || 'Other';
      functionBreakdown[func] = (functionBreakdown[func] || 0) + 1;
    });
    
    Object.entries(functionBreakdown).forEach(([func, count]) => {
      summaryRows.push({
        'Metric': `${func} Function`,
        'Value': count,
        'Percentage': roster.length > 0 ? `${Math.round((count / roster.length) * 100)}%` : '0%',
        'Description': `Members with ${func} function`
      });
    });
    
    // Tag usage statistics
    const tagUsage = {};
    roster.forEach(member => {
      if (member.tags && Array.isArray(member.tags)) {
        member.tags.forEach(tagId => {
          const tag = availableTags.find(t => t.id === tagId || t.id === String(tagId));
          const tagName = tag?.name || `Tag-${tagId}`;
          tagUsage[tagName] = (tagUsage[tagName] || 0) + 1;
        });
      }
    });
    
    Object.entries(tagUsage)
      .sort(([,a], [,b]) => b - a) // Sort by usage count descending
      .slice(0, 10) // Top 10 tags
      .forEach(([tagName, count]) => {
        summaryRows.push({
          'Metric': `Tag: ${tagName}`,
          'Value': count,
          'Percentage': roster.length > 0 ? `${Math.round((count / roster.length) * 100)}%` : '0%',
          'Description': `Members tagged with ${tagName}`
        });
      });
    
    return summaryRows;
  }

  /**
   * Convert member tags for export
   */
  convertMemberTagsForExport(memberTags, availableTags, targetSet) {
    if (memberTags && Array.isArray(memberTags)) {
      memberTags.forEach(tagId => {
        const tag = availableTags.find(t => t.id === tagId || t.id === String(tagId));
        if (tag && tag.name) {
          targetSet.add(tag.name);
        } else if (typeof tagId === 'string' && !tagId.match(/^\d+$/)) {
          // Fallback: if tag is already a string (name), use it directly
          targetSet.add(tagId);
        } else {
          // For UUID tags that don't exist, show a generic label
          if (typeof tagId === 'string' && tagId.match(/^[0-9a-f-]{36}$/)) {
            targetSet.add('Unknown Tag');
          } else {
            // For numeric IDs, show with prefix
            targetSet.add(`Tag-${tagId}`);
          }
        }
      });
    }
  }

  /**
   * Format worksheet with styling
   */
  formatWorksheet(worksheet, type) {
    if (!worksheet['!ref']) return;
    
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    
    // Set column widths based on content type
    const columnWidths = this.getColumnWidths(type);
    worksheet['!cols'] = columnWidths;
    
    // Format header row
    for (let col = range.s.c; col <= range.e.c; col++) {
      const headerCell = worksheet[XLSX.utils.encode_cell({ r: 0, c: col })];
      if (headerCell) {
        headerCell.s = {
          font: { bold: true },
          fill: { fgColor: { rgb: 'E8E8E8' } },
          alignment: { horizontal: 'center' }
        };
      }
    }
    
    // Format percentage columns (monthly allocations)
    if (type === 'members' || type === 'outcomes') {
      const monthColumns = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      for (let row = range.s.r + 1; row <= range.e.r; row++) {
        for (let col = range.s.c; col <= range.e.c; col++) {
          const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: col })];
          const headerCell = worksheet[XLSX.utils.encode_cell({ r: 0, c: col })];
          
          if (cell && headerCell && monthColumns.includes(headerCell.v)) {
            cell.z = '0.0%'; // Percentage format
          }
        }
      }
    }
  }

  /**
   * Get column widths for different worksheet types
   */
  getColumnWidths(type) {
    switch (type) {
      case 'members':
        return [
          { wch: 20 }, // Member Name
          { wch: 10 }, // Company
          { wch: 15 }, // Location
          { wch: 15 }, // Function
          { wch: 15 }, // Role
          { wch: 12 }, // Focus
          { wch: 15 }, // Department
          { wch: 30 }, // Applications
          { wch: 20 }, // Tags
          { wch: 30 }, // Outcome Name
          { wch: 12 }, // Project Code
          { wch: 25 }, // Outcome Applications
          ...Array(12).fill({ wch: 8 }) // Monthly columns
        ];
      case 'outcomes':
        return [
          { wch: 30 }, // Outcome Name
          { wch: 25 }, // Outcome Applications
          { wch: 20 }, // Member Name
          { wch: 10 }, // Company
          { wch: 15 }, // Location
          { wch: 15 }, // Function
          { wch: 15 }, // Role
          { wch: 12 }, // Focus
          { wch: 15 }, // Department
          { wch: 20 }, // Tags
          { wch: 20 }, // Role Name
          { wch: 15 }, // Milestone
          ...Array(12).fill({ wch: 8 }) // Monthly columns
        ];
      case 'summary':
        return [
          { wch: 25 }, // Metric
          { wch: 10 }, // Value
          { wch: 12 }, // Percentage
          { wch: 40 }  // Description
        ];
      default:
        return [];
    }
  }

  /**
   * Get export statistics
   */
  getExportStats() {
    return { ...this.exportStats };
  }

  /**
   * Reset export statistics
   */
  resetStats() {
    this.exportStats = {
      lastExportTime: null,
      totalExports: 0,
      lastExportSize: 0
    };
  }
}

export { RosterExportService };